import React from "react";
import { Redirect } from "react-router-dom";
import { useDashboard } from "../context/dashboard";
import { useAnalyticsDashboard } from "../hooks/useAnalyticsDashboard";
import { useLang } from "../context/lang";
import { useCognitoGroup } from "../hooks/useCognitoGroup";
import "../styles/dashboard.css";

const IFRAME_NAME = "StudentSpeakPostHogDashboard";

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfQuarter(date: Date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function startOfYear(date: Date) {
  return new Date(date.getFullYear(), 0, 1);
}

function daysAgo(days: number) {
  const date = new Date();
  date.setDate(date.getDate() - days + 1);
  return date;
}

function presetRange(preset: string): [Date, Date] {
  const today = new Date();
  const ranges: Record<string, [Date, Date]> = {
    last7d: [daysAgo(7), today],
    last30d: [daysAgo(30), today],
    last90d: [daysAgo(90), today],
    month: [startOfMonth(today), today],
    quarter: [startOfQuarter(today), today],
    ytd: [startOfYear(today), today],
  };
  return ranges[preset] || ranges.last30d;
}

function parseDate(value: string) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function embedOrigin(embedUrl: string | null) {
  if (!embedUrl) return null;
  try {
    return new URL(embedUrl).origin;
  } catch {
    return null;
  }
}

const PRESETS = [
  { id: "last7d", label: "7D", aria: "last7Days" },
  { id: "last30d", label: "30D", aria: "last30Days" },
  { id: "last90d", label: "90D", aria: "last90Days" },
  { id: "month", label: "MTD", aria: "mtd" },
  { id: "quarter", label: "QTD", aria: "qtd" },
  { id: "ytd", label: "YTD", aria: "ytd" },
];

export default function Dashboard() {
  const { available, loading, embedUrl, initialFrom, initialTo } =
    useDashboard();
  const { fetchDashboardRange, updateDashboardRange } = useAnalyticsDashboard();
  const { t, lang } = useLang();
  const { isMeritoGroup } = useCognitoGroup();

  const [fromValue, setFromValue] = React.useState("");
  const [toValue, setToValue] = React.useState("");
  const [activePreset, setActivePreset] = React.useState("last30d");
  const [rangeError, setRangeError] = React.useState("");
  const [iframeLoading, setIframeLoading] = React.useState(true);
  const [iframeLoaderText, setIframeLoaderText] = React.useState("");
  const [syncActive, setSyncActive] = React.useState(false);
  const [syncTitle, setSyncTitle] = React.useState("");
  const [syncDetail, setSyncDetail] = React.useState("");
  const [applyBusy, setApplyBusy] = React.useState(false);
  const [refreshBusy, setRefreshBusy] = React.useState(false);
  const [toastVisible, setToastVisible] = React.useState(false);
  const [toastText, setToastText] = React.useState("");
  const [toastTone, setToastTone] = React.useState<"info" | "success" | "error">(
    "info"
  );
  const embedRef = React.useRef<HTMLIFrameElement>(null);
  const embedBootstrappedRef = React.useRef(false);
  const toastTimerRef = React.useRef<number | null>(null);
  const iframeLoadTimeoutRef = React.useRef<number | null>(null);

  // Initialize/refresh language-dependent default strings
  React.useEffect(() => {
    setIframeLoaderText((prev) => prev || t("loadingEmbed"));
    setSyncTitle((prev) => prev || t("syncingRange"));
    setSyncDetail((prev) => prev || t("syncingFilters"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const showToast = React.useCallback(
    (message: string, tone: "info" | "success" | "error" = "info") => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      setToastText(message);
      setToastTone(tone);
      setToastVisible(true);
      toastTimerRef.current = window.setTimeout(() => {
        setToastVisible(false);
      }, 2800);
    },
    []
  );

  const setRange = React.useCallback(
    (from: Date, to: Date, preset = "custom") => {
      const fromStr = toDateInputValue(from);
      const toStr = toDateInputValue(to);
      setFromValue(fromStr);
      setToValue(toStr);
      setActivePreset(preset);

      const params = new URLSearchParams(window.location.search);
      params.set("from", fromStr);
      params.set("to", toStr);
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${params}`
      );
      setRangeError("");
    },
    []
  );

  const validateRange = React.useCallback(
    (from: string, to: string) => {
      const fromDate = parseDate(from);
      const toDate = parseDate(to);

      if (!fromDate || !toDate) {
        setRangeError(t("dateRangeInvalid"));
        return false;
      }

      if (fromDate > toDate) {
        setRangeError(t("startBeforeEnd"));
        return false;
      }

      setRangeError("");
      return true;
    },
    [t]
  );

  const refreshEmbed = React.useCallback(
    ({
      silent = false,
    }: { silent?: boolean; keepSync?: boolean } = {}) => {
      if (!embedUrl || !embedRef.current) return;

      setIframeLoading(true);
      setIframeLoaderText(t("refreshingDashboard"));
      setRefreshBusy(true);

      if (iframeLoadTimeoutRef.current) {
        window.clearTimeout(iframeLoadTimeoutRef.current);
      }

      embedRef.current.src = "about:blank";
      window.setTimeout(() => {
        if (embedRef.current) {
          embedRef.current.src = `${embedUrl}?_t=${Date.now()}`;
        }
      }, 60);

      iframeLoadTimeoutRef.current = window.setTimeout(() => {
        setIframeLoading(false);
        setSyncActive(false);
        setRefreshBusy(false);
        if (!silent) {
          showToast(t("dashboardRefreshed"), "success");
        }
      }, 15000);
    },
    [embedUrl, showToast, t]
  );

  const applyDashboardRange = React.useCallback(
    async (from: string, to: string) => {
      if (!validateRange(from, to)) {
        showToast(t("fixDates"), "error");
        return;
      }

      setApplyBusy(true);
      setSyncActive(true);
      setSyncTitle(t("syncingRange"));
      setSyncDetail(t("syncingFilters"));

      try {
        await updateDashboardRange(from, to);
        showToast(t("rangeSynced"), "success");
        refreshEmbed({ silent: true, keepSync: true });
      } catch (error: unknown) {
        setSyncActive(false);
        const axiosError = error as {
          response?: { data?: { error?: string } };
          message?: string;
        };
        const message =
          axiosError.response?.data?.error ||
          axiosError.message ||
          t("syncFailed");
        setRangeError(message);
        showToast(message, "error");
      } finally {
        setApplyBusy(false);
      }
    },
    [validateRange, updateDashboardRange, showToast, refreshEmbed, t]
  );

  const restoreInitialState = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const from = parseDate(params.get("from") || "");
    const to = parseDate(params.get("to") || "");

    if (from && to && from <= to) {
      setRange(from, to, "custom");
      return;
    }

    if (initialFrom && initialTo) {
      const fromInit = parseDate(initialFrom);
      const toInit = parseDate(initialTo);
      if (fromInit && toInit) {
        setRange(fromInit, toInit, "custom");
        return;
      }
    }

    const [defaultFrom, defaultTo] = presetRange("last30d");
    setRange(defaultFrom, defaultTo, "last30d");
  }, [initialFrom, initialTo, setRange]);

  React.useEffect(() => {
    if (!embedUrl || embedBootstrappedRef.current) return;
    embedBootstrappedRef.current = true;

    restoreInitialState();

    fetchDashboardRange()
      .then((dashboard) => {
        if (dashboard.from && dashboard.to) {
          const from = parseDate(dashboard.from);
          const to = parseDate(dashboard.to);
          if (from && to) {
            setRange(from, to, "custom");
          }
        }
      })
      .catch(() => {
        /* iframe still works without server sync */
      });

    refreshEmbed({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per embedUrl
  }, [embedUrl]);

  React.useEffect(() => {
    const origin = embedOrigin(embedUrl);
    if (!origin) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;

      const iframeHeight = Number(event.data && event.data.height);
      if (
        event.data &&
        event.data.event === "posthog:dimensions" &&
        event.data.name === IFRAME_NAME &&
        Number.isFinite(iframeHeight) &&
        embedRef.current
      ) {
        embedRef.current.height = String(Math.max(600, iframeHeight));
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedUrl]);

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (iframeLoadTimeoutRef.current) {
        window.clearTimeout(iframeLoadTimeoutRef.current);
      }
    };
  }, []);

  if (!loading && available === false) {
    if (isMeritoGroup) {
      return (
        <main className="dashboard-page dashboard-page--full app">
          <p className="subtitle">{t("embedFailed")}</p>
        </main>
      );
    }
    return <Redirect to="/students" />;
  }

  if (loading || !embedUrl) {
    return (
      <main className="dashboard-page dashboard-page--full app">
        <p className="subtitle">{t("loadingAnalytics")}</p>
      </main>
    );
  }

  return (
    <main className="dashboard-page dashboard-page--full app">
      <section className="topbar">
        <form
          className="toolbar"
          onSubmit={(event) => {
            event.preventDefault();
            setActivePreset("custom");
            applyDashboardRange(fromValue, toValue);
          }}
        >
          <div className="segmented" aria-label={t("datePresets")}>
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                className="segment"
                type="button"
                aria-pressed={activePreset === preset.id}
                aria-label={t(preset.aria)}
                onClick={() => {
                  const [from, to] = presetRange(preset.id);
                  setRange(from, to, preset.id);
                  applyDashboardRange(toDateInputValue(from), toDateInputValue(to));
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="date-fields">
            <div className="date-field">
              <label htmlFor="from-date">{t("from")}</label>
              <input
                id="from-date"
                name="from"
                type="date"
                required
                value={fromValue}
                onChange={(event) => setFromValue(event.target.value)}
              />
            </div>
            <div className="date-field">
              <label htmlFor="to-date">{t("to")}</label>
              <input
                id="to-date"
                name="to"
                type="date"
                required
                value={toValue}
                onChange={(event) => setToValue(event.target.value)}
              />
            </div>
          </div>

          <button
            className="button primary"
            type="submit"
            aria-busy={applyBusy}
            disabled={applyBusy}
          >
            <span className="btn-label">{t("apply")}</span>
            <span className="spinner" aria-hidden="true" />
          </button>
          <button
            className="button icon-only"
            type="button"
            title={t("refreshDashboard")}
            aria-label={t("refreshDashboard")}
            aria-busy={refreshBusy}
            disabled={refreshBusy}
            onClick={() => refreshEmbed()}
          >
            <span className="btn-label" aria-hidden="true">
              <svg
                className="icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M15.312 4.688a7 7 0 10 1.708 7.21 1 1 0 10-1.92-.56 5 5 0 11-1.207-5.225L11.5 8H17V2.5l-1.688 2.188z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <span className="spinner" aria-hidden="true" />
          </button>
        </form>
      </section>

      {rangeError ? (
        <p className="notice" data-visible="true" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 102 0V6zm-1 8a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <span>{rangeError}</span>
        </p>
      ) : null}

      <section
        className="dashboard-shell"
        aria-label={t("embeddedDashboard")}
        aria-busy={syncActive}
      >
        <div
          className="sync-bar"
          aria-hidden="true"
          data-active={syncActive ? "true" : "false"}
        />

        <div
          className="iframe-wrap"
          data-loading={iframeLoading ? "true" : "false"}
        >
          <div className="iframe-loader" aria-hidden="true">
            <div className="skeleton-grid">
              <div className="skeleton wide" />
              <div className="skeleton wide" />
              <div className="skeleton wide" />
              <div className="skeleton tall" />
              <div className="skeleton tall wide" />
              <div className="skeleton tall" />
              <div className="skeleton wide" />
              <div className="skeleton wide" />
              <div className="skeleton wide" />
              <div className="skeleton full" />
            </div>
            <div className="iframe-loader-caption">
              <span className="spinner-sm" aria-hidden="true" />
              <span>{iframeLoaderText}</span>
            </div>
          </div>
          <iframe
            ref={embedRef}
            name={IFRAME_NAME}
            title={t("embeddedDashboard")}
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups"
            src={embedUrl}
            onLoad={() => {
              if (
                embedRef.current?.src === "about:blank" ||
                embedRef.current?.src.endsWith("about:blank")
              ) {
                return;
              }
              if (iframeLoadTimeoutRef.current) {
                window.clearTimeout(iframeLoadTimeoutRef.current);
                iframeLoadTimeoutRef.current = null;
              }
              setIframeLoading(false);
              setSyncActive(false);
              setRefreshBusy(false);
            }}
            onError={() => {
              setIframeLoading(false);
              showToast(t("embedFailed"), "error");
            }}
          />
          <div
            className="sync-overlay"
            aria-hidden="true"
            data-active={syncActive ? "true" : "false"}
          >
            <div className="sync-card">
              <div className="sync-spinner" aria-hidden="true" />
              <div className="sync-card-text">
                <strong>{syncTitle}</strong>
                <span>{syncDetail}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        className="toast"
        role="status"
        aria-live="polite"
        data-visible={toastVisible ? "true" : "false"}
        data-tone={toastTone}
      >
        <span className="toast-dot" aria-hidden="true" />
        <span>{toastText}</span>
      </div>
    </main>
  );
}
